/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  Card,
  HStack,
  Image,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider';
import { BenefitCard } from '../components/BenefitCard';
import React from 'react';
import { axiosInstance } from '@partiu/api/axios';
import { groupByCategory } from './forms/BenefitsList';
import { useModulesSuperApp, useProductsAndPlans } from '@partiu/api/dashboard/hooks';
import { StepNavigation } from '../components/StepNavigation';
import { ModuleItem } from './Template';


interface PlanList {
  plan: any;
  apps: {
    name: string;
    icon: string;
  }[];
}

interface Step4Props {
  activeStep: number;
  setActiveStep: (step: number) => void;
  onNext: () => void;
  onPrevious: () => void;
}

export const badges = [
  {
    name: 'Bronze',
    level: 1,
    rank: 1,
    image: 'badges/bronze.png',
  },
  {
    name: 'Prata',
    level: 2,
    rank: 2,
    image: 'badges/prata.png',
  },
  {
    name: 'Ouro',
    level: 3,
    rank: 71,
    image: 'badges/ouro.png',
  },
  {
    name: 'Safira',
    level: 4,
    rank: 6,
    image: 'badges/safira.png',
  },
  {
    name: 'Diamante',
    level: 5,
    rank: 4,
    image: 'badges/diamante.png',
  },
];

export const Step4 = ({
  activeStep,
  setActiveStep,
  onNext,
  onPrevious,
}: Step4Props) => {
  const { colors, id } = useTheme();
  const { data: modules } = useModulesSuperApp();
  const { data: products } = useProductsAndPlans();

  const toast = useToast();

  // Estados principais
  const [plans, setPlans] = React.useState<PlanList[]>([]);
  const [selectedPlan, setSelectedPlan] = React.useState<PlanList | null>(null);
  const [loading, setLoading] = React.useState(false);

  const groupedBenefits = React.useMemo(() =>
    groupByCategory(modules || []),
    [modules]
  );

  // Carregar filtros dos planos
  const handleGetFilters = React.useCallback(async () => {
    if (!id || !modules) return;

    try {
      const { data: response } = await axiosInstance.get('/agreement_staff/modules/filters/', {
        params: { agreement_id: id },
      });

      const findPlans = response.find((item: any) => item.key === 'external_plan');

      if (!findPlans?.values) {
        toast({
          title: 'Nenhum plano encontrado',
          description: 'Verifique se existem planos configurados.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const plansList = findPlans.values.map((item: any) => {
        const findApps = modules.filter((app: any) =>
          app.externalPlan?.includes(item.name)
        );

        return {
          plan: item,
          apps: findApps || [],
        };
      });

      setPlans(plansList);
    } catch (error: any) {
      console.error('Error loading filters:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao carregar planos';

      toast({
        title: 'Erro ao carregar planos',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [id, modules, toast]);

  // Salvar configurações
  const handleSaveFilter = React.useCallback(async () => {
    if (!modules || !plans.length) return false;

    try {
      setLoading(true);

      await Promise.all(
        modules.map(async (item: any) => {
          const plansFound: string[] = [];

          plans.forEach((planItem) => {
            if (planItem.apps.find((app: any) => app.id === item.id)) {
              plansFound.push(planItem.plan.name);
            }
          });

          await axiosInstance.patch(`/agreement_staff/modules/${item.id}/`, {
            external_plan: plansFound,
          });
        })
      );


      toast({
        title: 'Configurações salvas!',
        description: 'Os planos foram configurados com sucesso.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      return true;
    } catch (error: any) {
      console.error('Error saving filters:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao salvar configurações';

      toast({
        title: 'Erro ao salvar',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      return false;
    } finally {
      setLoading(false);
    }
  }, [modules, plans, toast]);

  // Gerenciar benefícios do plano
  const handleToggleBenefit = React.useCallback((benefit: any) => {
    if (!selectedPlan) return;

    const updatedPlans = plans.map((planItem) => {
      if (planItem.plan.name !== selectedPlan.plan.name) return planItem;

      const isIncluded = planItem.apps.find((app: any) => app.id === benefit.id);

      const updatedApps = isIncluded
        ? planItem.apps.filter((app: any) => app.id !== benefit.id)
        : [...planItem.apps, benefit];

      return { ...planItem, apps: updatedApps };
    });

    setPlans(updatedPlans);
    setSelectedPlan(updatedPlans.find(p => p.plan.name === selectedPlan.plan.name) || null);
  }, [selectedPlan, plans]);

  // Efeitos
  React.useEffect(() => {
    handleGetFilters();
  }, [handleGetFilters]);

  return (
    <Card
      height="100%"
      padding={4}
      width="100%"
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <Text textAlign="center" fontSize="md" fontWeight="bold" color={colors.primaryColor} mb={3}>
        Configuração de Planos e Benefícios
      </Text>

      {/* Área principal com scroll único */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        gap="8px"
        pb={2}
      >
        {/* Textos explicativos compactos */}
        <Box
          bg="green.50"
          p={3}
          borderRadius="md"
          borderLeft="3px solid"
          borderLeftColor="green.400"
          mb={2}
        >
          <Text fontSize="14px" lineHeight="1.5" color="gray.700" mb={2}>
            Configure quais benefícios estarão disponíveis em cada plano:
          </Text>

          <VStack align="start" spacing={1}>
            <Text fontSize="13px" lineHeight="1.4" color="gray.700">
              <Text as="span" fontWeight="bold" color="green.600">1.</Text> Visualize todos os planos e selecione qual deseja configurar
            </Text>
            <Text fontSize="13px" lineHeight="1.4" color="gray.700">
              <Text as="span" fontWeight="bold" color="green.600">2.</Text> Personalize benefícios de acordo com seu público-alvo
            </Text>
            <Text fontSize="13px" lineHeight="1.4" color="gray.700">
              <Text as="span" fontWeight="bold" color="green.600">3.</Text> Salve as configurações ao finalizar
            </Text>
          </VStack>
        </Box>

        {/* Conteúdo principal */}
        {!selectedPlan && (
          <VStack spacing={4} width="100%">
            <Text fontSize="sm" fontWeight="semibold" textAlign="center" color={colors.primaryColor}>
              Selecione um plano para configurar os benefícios
            </Text>

            {plans.length === 0 ? (
              <Text color="gray.500" fontSize="sm">Carregando planos disponíveis...</Text>
            ) : (
              <HStack
                spacing={6}
                justifyContent="center"
                width="100%"
                flexWrap="wrap"
                alignItems="flex-start"
              >
                {plans.map((planItem, index) => (
                  <Card
                    key={index}
                    maxWidth="280px"
                    padding={4}
                    borderRadius="12px"
                    border="2px solid transparent"
                    cursor="pointer"
                    transition="all 0.3s ease"
                    _hover={{
                      borderColor: colors.primaryColor,
                      transform: "translateY(-4px)",
                      boxShadow: `0 12px 25px -6px ${colors.primaryColor}30`,
                    }}
                    onClick={() => setSelectedPlan(planItem)}
                    bg="white"
                    boxShadow="0 2px 12px rgba(0,0,0,0.06)"
                  >
                    <VStack spacing={3} align="center">
                      {/* Header do plano */}
                      <HStack spacing={2} align="center" justify="center">
                        <Text
                          fontWeight="bold"
                          fontSize="lg"
                          color={colors.primaryColor}
                          textAlign="center"
                        >
                          {planItem.plan.name}
                        </Text>

                        {badges.find((badge) => badge.level === planItem.plan.rank) && (
                          <Image
                            src={badges.find((badge) => badge.level === planItem.plan.rank)?.image}
                            alt={badges.find((badge) => badge.level === planItem.plan.rank)?.name}
                            height="32px"
                            filter="drop-shadow(0 3px 6px rgba(0,0,0,0.15))"
                          />
                        )}
                      </HStack>

                      {/* Preview do iPhone compacto */}
                      <Box
                        position="relative"
                        width="220px"
                        height="300px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Image
                          position="absolute"
                          zIndex={1}
                          src="/assets/iphone.png"
                          width="100%"
                          height="100%"
                          objectFit="contain"
                          alt="Super App"
                          opacity={0.9}
                          left="50%"
                          top="50%"
                          transform="translate(-50%, -50%)"
                        />
                        <Box
                          position="absolute"
                          top="40px"
                          left="50%"
                          transform="translateX(-50%)"
                          zIndex={2}
                          width="160px"
                          height="220px"
                          overflowY="auto"
                          display="flex"
                          flexWrap="wrap"
                          justifyContent="center"
                          alignContent="flex-start"
                          paddingTop="12px"
                          sx={{
                            '&::-webkit-scrollbar': { display: 'none' },
                            '-ms-overflow-style': 'none',
                            'scrollbar-width': 'none',
                          }}
                        >
                          {groupByCategory(planItem.apps || [])?.map((app: any, appIndex) => (
                            <Box key={appIndex} transform="scale(0.65)">
                              <ModuleItem
                                maskColor={colors.primaryColor}
                                item={app.benefits}
                                moduleName={app.category}
                              />
                            </Box>
                          ))}
                          {planItem.apps.length === 0 && (
                            <VStack spacing={1} justify="center" align="center" height="100%" width="100%">
                              <Text fontSize="xs" color="gray.400" textAlign="center">
                                📱 Sem benefícios
                              </Text>
                            </VStack>
                          )}
                        </Box>
                      </Box>

                      {/* Informações do plano compactas */}
                      <VStack spacing={2} width="100%">
                        <HStack justify="space-between" width="100%">
                          <Text fontSize="xs" color="gray.600" fontWeight="medium">
                            Benefícios ativos:
                          </Text>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color={planItem.apps.length > 0 ? "green.500" : "gray.400"}
                          >
                            {planItem.apps.length}
                          </Text>
                        </HStack>

                        <Button
                          size="sm"
                          width="100%"
                          borderColor={colors.primaryColor}
                          color={colors.primaryColor}
                          variant="outline"
                          fontWeight="semibold"
                          borderRadius="8px"
                          _hover={{
                            bg: colors.primaryColor,
                            color: "white",
                            transform: "translateY(-1px)",
                          }}
                          transition="all 0.2s ease"
                        >
                          Configurar Plano
                        </Button>

                        {planItem.apps.length === 0 && (
                          <Text
                            fontSize="xs"
                            color="orange.500"
                            textAlign="center"
                            fontWeight="medium"
                          >
                            ⚠️ Nenhum benefício configurado
                          </Text>
                        )}
                      </VStack>
                    </VStack>
                  </Card>
                ))}
              </HStack>
            )}
          </VStack>
        )}

        {selectedPlan && (
          <HStack spacing={4} height="100%" width="100%" align="flex-start">
            {/* Lado esquerdo - Lista de benefícios */}
            <Box width="60%" height="100%" overflowY="auto">
              <VStack align="start" spacing={3} mb={4}>
                <HStack spacing={2} align="center">
                  <Text fontWeight="bold" fontSize="lg" color={colors.primaryColor}>
                    Editando: {selectedPlan.plan.name}
                  </Text>
                  {badges.find((badge) => badge.level === selectedPlan.plan.rank) && (
                    <Image
                      src={badges.find((badge) => badge.level === selectedPlan.plan.rank)?.image}
                      alt={badges.find((badge) => badge.level === selectedPlan.plan.rank)?.name}
                      height="24px"
                    />
                  )}
                </HStack>
                <Text fontSize="xs" color="gray.600">
                  Clique nos benefícios abaixo para adicionar ou remover do plano
                </Text>
              </VStack>

              {/* Lista de produtos disponíveis */}
              {products && products.length > 0 && (
                <Box mb={4}>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    Lista de produtos disponíveis para o plano selecionado.
                  </Text>
                  <HStack flexWrap="wrap" spacing={2}>
                    {products.map((product: any, index) => (
                      <Card
                        key={index}
                        align="center"
                        display="flex"
                        padding="8px 16px"
                        justifyContent="center"
                      >
                        <Text fontWeight="bold" color={colors.primaryColor} fontSize="sm">
                          {product.planSlugIntegration}
                        </Text>
                      </Card>
                    ))}
                  </HStack>
                </Box>
              )}

              {/* Benefícios por categoria */}
              {groupedBenefits.map((category) => (
                <Box key={category.category} mb={4}>
                  <Text fontWeight="bold" fontSize="md" mb={3}>
                    {category.category}
                  </Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {category.benefits.map((benefit: any) => {
                      const isIncluded = selectedPlan.apps.find((app: any) => app.id === benefit.id);
                      return (
                        <BenefitCard
                          key={benefit.id || benefit.title}
                          canEdit={false}
                          small
                          inverted
                          disabled={!!isIncluded}
                          color={colors.primaryColor}
                          title={benefit.title}
                          image={benefit.icon}
                          onClick={() => handleToggleBenefit(benefit)}
                        />
                      );
                    })}
                  </HStack>
                </Box>
              ))}
            </Box>

            {/* Lado direito - Preview do plano */}
            <Box
              width="40%"
              height="100%"
              display="flex"
              justifyContent="center"
              alignItems="flex-start"
              position="sticky"
              top="0"
            >
              <VStack spacing={3} align="center" width="100%">
                {/* Título e badge */}
                <HStack align="center" spacing={2} justify="center">
                  <Text fontWeight="bold" fontSize="md" color={colors.primaryColor}>
                    Preview - {selectedPlan.plan.name}
                  </Text>
                  {badges.find((badge) => badge.level === selectedPlan.plan.rank) && (
                    <Image
                      src={badges.find((badge) => badge.level === selectedPlan.plan.rank)?.image}
                      alt={badges.find((badge) => badge.level === selectedPlan.plan.rank)?.name}
                      height="28px"
                    />
                  )}
                </HStack>

                {/* iPhone Preview */}
                <Box position="relative" width="200px" height="380px">
                  <Image
                    position="absolute"
                    zIndex={1}
                    src="/assets/iphone.png"
                    width="100%"
                    height="100%"
                    objectFit="contain"
                    alt="Super App"
                  />
                  <Box
                    position="absolute"
                    top="40px"
                    left="50%"
                    transform="translateX(-50%)"
                    zIndex={2}
                    width="140px"
                    height="280px"
                    overflowY="auto"
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="center"
                    alignContent="flex-start"
                    paddingTop="12px"
                    sx={{
                      '&::-webkit-scrollbar': {
                        display: 'none',
                      },
                      '-ms-overflow-style': 'none',
                      'scrollbar-width': 'none',
                    }}
                  >
                    {groupByCategory(selectedPlan.apps || [])?.map((app: any, appIndex) => (
                      <ModuleItem
                        key={appIndex}
                        maskColor={colors.primaryColor}
                        item={app.benefits}
                        moduleName={app.category}
                      />
                    ))}
                    {selectedPlan.apps?.length === 0 && (
                      <Text fontSize="xs" fontWeight="bold" textAlign="center" marginTop={10} color="gray.500">
                        Nenhum Benefício disponível
                      </Text>
                    )}
                  </Box>
                </Box>
              </VStack>
            </Box>
          </HStack>
        )}
      </Box>

      {/* Navegação */}
      <StepNavigation
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        onNext={async () => {
          const success = await handleSaveFilter();
          if (success) {
            onNext();
          }
        }}
        onPrevious={() => {
          // Se tem plano selecionado, volta para lista de planos
          if (selectedPlan) {
            setSelectedPlan(null);
          } else {
            // Se não tem plano selecionado, vai para step anterior
            onPrevious();
          }
        }}
        isFirstStep={false}
        isLastStep={true}
        isLoading={loading}
        submitButtonText="Finalizar Configuração"
      />
    </Card>
  );
};
